// Day-level resolution helpers for calendar views (year grid, month zoom).
//
// Ownership is resolved per INSTANT, narrowest concrete decision first:
//
//   1. Paired Eve/Day blocks. `paired-day-split` is a 24-hour block that
//      starts at the rule's split time and therefore straddles two calendar
//      days (noon Dec 24 → noon Dec 25). One block owns only PART of each
//      day it touches, so a single day can be split between two different
//      decisions — Christmas Day belongs to the Christmas Eve block until
//      noon and to the Christmas Day block after it.
//   2. Whole-day holiday decisions — the narrowest covering holiday whose
//      rule resolves concretely (fixed / alternating / shared / split /
//      break halves). Hours a paired block doesn't cover fall through to
//      here, so Christmas Eve morning follows the Winter Break decision.
//   3. The parenting rotation.

import { resolveRotationParent } from './rotation.js'
import { holidaysCoveringDate } from './dates.js'
import { parentForDate } from './proseRenderer.js'

import { MIN_PER_DAY } from './weekMath.js'

// The calendar day each paired block STARTS on, as [monthIndex, day]. The
// block runs from the rule's splitTime on that date for 24 hours. Keep in
// sync with PAIRED_DAY_RANGES in seed/holidays.js, which carries the same
// pairs as display strings for the legal prose.
const PAIRED_BLOCK_START = {
  'christmas-eve': [11, 24],
  'christmas-day': [11, 25],
  'new-years-eve': [11, 31],
  'new-years-day': [0, 1],
}

// Deliberately NOT weekMath's timeToMin: this reads a paired-day rule's
// splitTime, where an absent or malformed value means noon, not midnight.
function timeToMin(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || ''))
  if (!m) return 720 // noon
  return Number(m[1]) * 60 + Number(m[2])
}

function otherOf(p) {
  return p === 'A' ? 'B' : 'A'
}

function midnight(y, m, d) {
  return new Date(y, m, d).getTime()
}

// Whole days between two calendar dates, DST-safe (Math.round absorbs the
// ±1h an elapsed-ms difference picks up across a time change).
function dayDiff(fromMs, toMs) {
  return Math.round((toMs - fromMs) / 86400000)
}

// Segments of `date` owned by paired Eve/Day blocks, in minutes from the
// date's midnight. The owner alternates on the parity of the year the block
// STARTS in, so the Dec 31 → Jan 1 block keeps one owner across the year
// boundary instead of flipping parents halfway through.
export function pairedDaySegments(date, holidays = []) {
  const segs = []
  const dayMs = midnight(date.getFullYear(), date.getMonth(), date.getDate())
  for (const h of holidays) {
    const rule = h && h.rule
    if (!rule || rule.kind !== 'paired-day-split') continue
    const start = PAIRED_BLOCK_START[h.id]
    if (!start) continue
    const splitMin = timeToMin(rule.splitTime)
    // A block starting last year can reach into this one (Dec 31 → Jan 1).
    for (const startYear of [date.getFullYear() - 1, date.getFullYear()]) {
      const offset = dayDiff(midnight(startYear, start[0], start[1]), dayMs) * MIN_PER_DAY
      // Both spans measured from the block-start day's midnight.
      const lo = Math.max(splitMin, offset)
      const hi = Math.min(splitMin + MIN_PER_DAY, offset + MIN_PER_DAY)
      if (hi <= lo) continue
      segs.push({
        startMin: lo - offset,
        endMin: hi - offset,
        partner: startYear % 2 === 0 ? rule.evenParent : otherOf(rule.evenParent),
        source: h.name || 'Holiday',
      })
    }
  }
  return segs.sort((a, b) => a.startMin - b.startMin)
}

// The whole-day holiday decision for `date`, if any. Paired blocks are
// skipped here — they resolve as sub-day segments — so the hours they leave
// uncovered still defer outward to the surrounding break.
function wholeDayDecision(date, holidays, opts) {
  const hits = holidays.length
    ? holidaysCoveringDate(date, holidays, { jurisdiction: opts.jurisdiction })
    : []
  const name = hits[0] && hits[0].holiday ? (hits[0].holiday.name || 'Holiday') : null
  for (const hit of hits) {
    const rule = hit.holiday.rule
    if (!rule || rule.kind === 'paired-day-split') continue
    const who = parentForDate(rule, date, hit)
    if (who === 'A' || who === 'B' || who === 'shared' || who === 'split') {
      return { name, who, sourceName: hit.holiday.name || 'Holiday' }
    }
  }
  return { name, who: null, sourceName: null }
}

// Ownership of `date` as ordered, merged runs covering midnight to midnight.
// → [ { partner: 'A'|'B'|'shared'|'split'|null, startMin, endMin,
//       source: string|null,       // holiday name, or null when the rotation decides
//       fromHoliday: boolean } ]
export function daySegments(date, rotation, holidays = [], opts = {}) {
  const step = opts.step || 30
  const paired = pairedDaySegments(date, holidays)
  const decision = wholeDayDecision(date, holidays, opts)

  // Sample boundaries: the regular grid plus every paired-block edge, so a
  // handover at an off-grid time still lands exactly.
  const marks = new Set([0, MIN_PER_DAY])
  for (let t = step; t < MIN_PER_DAY; t += step) marks.add(t)
  for (const s of paired) { marks.add(s.startMin); marks.add(s.endMin) }
  const bounds = [...marks].filter((t) => t >= 0 && t <= MIN_PER_DAY).sort((a, b) => a - b)

  const y = date.getFullYear()
  const m = date.getMonth()
  const d = date.getDate()
  const runs = []
  for (let i = 0; i < bounds.length - 1; i++) {
    const startMin = bounds[i]
    const endMin = bounds[i + 1]
    const mid = (startMin + endMin) / 2

    let partner = null
    let source = null
    let fromHoliday = false
    const block = paired.find((s) => mid >= s.startMin && mid < s.endMin)
    if (block) {
      partner = block.partner
      source = block.source
      fromHoliday = true
    } else if (decision.who) {
      partner = decision.who
      source = decision.sourceName
      fromHoliday = true
    } else if (rotation) {
      partner = resolveRotationParent(new Date(y, m, d, 0, mid), rotation)
    }

    const last = runs[runs.length - 1]
    if (last && last.partner === partner && last.source === source) last.endMin = endMin
    else runs.push({ partner, startMin, endMin, source, fromHoliday })
  }
  return runs
}

// Resolve one calendar day for painting.
// → {
//     holidayName: string|null,   // narrowest holiday covering the date, if any
//     holidayWho:  'A'|'B'|'shared'|'split'|null,  // set only when ONE holiday
//                                 // decision owns the whole day
//     aHours, bHours,             // hours per party when the day is not wholly
//                                 // owned by a single holiday decision
//     runs,                       // ordered ownership runs (see daySegments)
//   }
export function resolveDay(date, rotation, holidays = [], opts = {}) {
  const runs = daySegments(date, rotation, holidays, { ...opts, step: opts.step || 60 })
  const holidayName = holidays.length
    ? wholeDayDecision(date, holidays, opts).name
    : null

  // A day counts as holiday-owned only when every minute of it comes from a
  // holiday decision AND they all name the same party — otherwise it paints
  // as a split day so a noon handover stays visible.
  const wholeDay = runs.length > 0
    && runs.every((r) => r.fromHoliday && r.partner === runs[0].partner)
  if (wholeDay) {
    return { holidayName, holidayWho: runs[0].partner, aHours: 0, bHours: 0, runs }
  }

  let aMin = 0
  let bMin = 0
  for (const r of runs) {
    if (r.partner === 'A') aMin += r.endMin - r.startMin
    else if (r.partner === 'B') bMin += r.endMin - r.startMin
  }
  return { holidayName, holidayWho: null, aHours: aMin / 60, bHours: bMin / 60, runs }
}

// Ownership runs across one day at 30-minute resolution, for the zoomed
// day breakdown ("Jane 12:00 am - 2:00 pm, David 2:00 pm - end of day").
// Pass `holidays` so paired Eve/Day blocks and holiday decisions appear;
// omit it for a rotation-only view.
// → [ { partner: 'A'|'B'|null, startMin, endMin, source, fromHoliday } ]
export function dayRuns(date, rotation, holidays = [], opts = {}) {
  return daySegments(date, rotation, holidays, opts)
}
